import { AttachFile, InsertDriveFileOutlined } from "@mui/icons-material";
import { Box } from "@mui/material";
import { useState, useEffect, useRef } from "react";
import Dropzone, { useDropzone } from "react-dropzone";
import { toast } from "react-hot-toast";
import { sanitizeFileName } from "../utils/assistantUtils";

const SUPPORTED_FILES = [
  "pdf",
  "zip",
  "x-compressed-zip",
  "x-zip-compressed",
  "vnd.openxmlformats-officedocument.wordprocessingml.document",
  "msword",
];

const DEFAULT_FILES = {
    "application/pdf": [],
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [],
    "application/zip": [],
    "application/x-zip-compressed": [],
    "application/msword": [],
};
const XLSX = "vnd.openxmlformats-officedocument.spreadsheetml.sheet";

const FileDrop = ({
  setUploadedFiles,
  disabledFields,
  uploadedFiles,
  loading,
  fileNamesSet,
  setFileNamesSet,
    intentName
}) => {
  const [isDragging, setIsDragging] = useState(false);
    const isSb08 = intentName.toLowerCase() === "sb08intent";
    const accept = isSb08 ? { ...DEFAULT_FILES, [`application/${XLSX}`]: [] } : DEFAULT_FILES;
  const dragCounter = useRef(0);
 
  const onDrop = (acceptedFiles, rejectedFiles) => {
    const newFiles = [];
    const newFileNamesSet = new Set(fileNamesSet);

    acceptedFiles.forEach((acceptedFile) => {
      const newFileName = sanitizeFileName(acceptedFile.name);
      const file =  new File([acceptedFile], newFileName, {
        type: acceptedFile.type,
        lastModified: acceptedFile.lastModified
      })
      const fileKey = `${file.name}.${file.type}`;
      if (!newFileNamesSet.has(fileKey)) {
        newFileNamesSet.add(fileKey);
        newFiles.push(file);
      } else {
        if(intentName == "referenceextraction"){
          toast.error("Duplicate file names are present in the request, please remove duplicates and submit request again", {
            duration: 3000,
          });
        }else {
          toast.error(`Duplicate file "${file.name}"`, {
            duration: 1500,
          });
        }
      }
    });

    if (rejectedFiles.length > 0) {
        rejectedFiles.forEach(({ errors }) => {
            const sizeError = errors.find((error) => error.code === "size-too-large");
            const typeError = errors.find((error) =>
                error.code === "type-not-supported" || error.code === "file-invalid-type"
            );
            if (sizeError) {
                toast.error("File is larger than 250MB", { duration: 1500 });
            } else if (typeError) {
                toast.error("File type not supported", { duration: 1500 });
            }
        });
    }
    setUploadedFiles((prev) => [...prev, ...newFiles]);
    setFileNamesSet(newFileNamesSet);
  };

  const validateSelectedFiles = (file) => {
    if (file.size > 250 * 1024 * 1024) {
      return {
        code: "size-too-large",
        message:
          "The uploaded document exceeds the allowed size. Please upload a smaller file.",
      };
    }

    const fileType = file.type.split("/")[1]?.toLowerCase();
    const isZip =
      file.name.endsWith(".zip") &&
      (fileType === "zip" ||
        fileType === "x-compressed-zip" ||
        fileType === "x-zip-compressed");

    if (!SUPPORTED_FILES.includes(fileType) && !isZip && !(isSb08 && fileType === XLSX)) {
      return {
        code: "type-not-supported",
        message: "File type not supported",
      };
    }

    return null;
  };
 
  // Global drag event listeners with a counter to prevent flickering
  useEffect(() => {
    const handleDragEnter = (e) => {
      e.preventDefault();
      dragCounter.current++;
      if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
        setIsDragging(true);
      }
    };
 
    const handleDragLeave = (e) => {
      e.preventDefault();
      dragCounter.current--;
      // Only disable the overlay when counter reaches zero
      if (dragCounter.current === 0) {
        setIsDragging(false);
      }
    };
 
    const handleDrop = (e) => {
      e.preventDefault();
      dragCounter.current = 0;
      setIsDragging(false);
    };
 
    window.addEventListener("dragenter", handleDragEnter);
    window.addEventListener("dragleave", handleDragLeave);
    window.addEventListener("drop", handleDrop);
 
    return () => {
      window.removeEventListener("dragenter", handleDragEnter);
      window.removeEventListener("dragleave", handleDragLeave);
      window.removeEventListener("drop", handleDrop);
    };
  }, []);
 
  // Hidden dropzone for file dialog functionality (triggered by the attach icon)
  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    noClick: true,
    noKeyboard: true,
    validator: validateSelectedFiles,
    accept,
    disabled: loading || disabledFields.fileDrop,
  });
 
  return (
    <>
      {/* Hidden dropzone for file dialog */}
      <Box {...getRootProps()} sx={{ display: "none" }}>
        <input {...getInputProps()} />
      </Box>
 
      {/* Attach icon triggers the file dialog */}
      <AttachFile
        onClick={open}
        fontSize="small"
        sx={{
          display: "flex",
          alignSelf: "center",
          cursor: disabledFields.fileDrop || loading ? "not-allowed" : "pointer",
          color: disabledFields.fileDrop || loading ? "#8B8B8B" : "#005cb9",
        }}
      />
 
      {/* Render the full-screen dropzone overlay only while dragging */}
      {isDragging && !disabledFields.fileDrop && (
        <Dropzone
          onDrop={onDrop}
          noClick
          noKeyboard
          validator={validateSelectedFiles}
            accept={accept}
          disabled={loading || disabledFields.fileDrop}
        >
          {({ getRootProps, getInputProps }) => (
            <Box
              {...getRootProps()}
              sx={{
                position: "fixed",
                top: 0,
                right: { md: "5%", xs: "10%" },
                bottom: 0,
                left: { md: "10%", xs: "12%", lg: "25%" },
                zIndex: 1000,
                background: "rgba(255, 255, 255, 0.8)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <input {...getInputProps()} style={{ display: "none" }} />
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <InsertDriveFileOutlined sx={{ color: "#005cb9", fontSize: "30px" }} />
                <p
                  style={{
                    color: "grey",
                    fontSize: "25px",
                    textAlign: "center",
                    marginTop: "10px",
                    marginBottom: "0px",
                  }}
                >
                  Add Documents
                </p>
                <span style={{ color: "grey", fontSize: "10px", padding: 0, margin: 0 }}>
                  Drop your files to add in textbox
                </span>
              </Box>
            </Box>
          )}
        </Dropzone>
      )}
    </>
  );
};

export default FileDrop;